import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { makeStyles, useTheme } from '@mui/styles';

import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormLabel from '@mui/material/FormLabel';
import CircularProgress from '@mui/material/CircularProgress';

import { get as _get } from 'lodash';

import Auth from '../../../data/auth-service';
import DateTimePicker from '../../../components/styled/datetimepicker';
import Validator, { useValidate, checkValidity } from '../../../components/validator';
import PcapService from '../../../data/pcap-service';
import SensorsService from '../../../data/sensors-service';
import CMCsDataService, { CmcContext } from '../../../data/cmcs-service';
import Moment from 'moment';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    control: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    datePicker: {
        maxWidth: 165,
        display: 'inline-block',
    },
    notCrammed: {
        paddingBottom: 5,
    },
    primaryButtonGroup: {
        '& .MuiToggleButton-root': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
        '& .MuiToggleButton-root.Mui-selected': {
            background: theme.palette.primary.main,
            color: theme.palette.common.white,
        },
        '& .Mui-disabled': {
            opacity: 0.5,
        }
    },
    info: {
        ...theme.typography.caption,
    },
    minmax: {
        // ...theme.typography.caption,
        '& span': {
            color: theme.palette.secondary.main,
            paddingLeft: 10,
            paddingRight: 10,
        }
    },
    warningMessage: {
        background: '#000',
        padding: '5px',
        border: `1px solid ${theme.palette.warning.main}`,
    },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const protos = ['any', 'tcp', 'udp', 'icmp', 'icmpv6'];
const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

const CreatePcap = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const daForm = useValidate({});

    const [stats, setStats] = useState({});
    const [info, setInfo] = useState({});
    const [interfaces, setInterfaces] = useState(['any']);
    const [sensors, setSensors] = useState();
    const [gotSensors, setGotSensors] = useState({});
    const [matchby, setMatchby] = useState('Timestamp');
    const [data, setData] = useState({
        host: '',
        timestamp: Moment().unix(),
        end: Moment().unix() + 11,
        interfaces: ['any'],
        proto: 'any',
        format: 'pcap'
    });

    const sensorPerms = Auth.permissions.get('Sensors');
    const cmcPerms = Auth.permissions.get('CMC');

    // cmc is an object { uuid, name } - stored in page profile as uuid
    const { cmc, setCmc } = useContext(CmcContext);
    // cmcs is an object { uuid: name, uuid: name }
    const [cmcs, setCmcs] = useState();

    const handleUpdateData = (e, validID, valid) => {
        validID && daForm.set({ ...daForm.get(), [validID]: valid });
        setData({
            ...data,
            [e.target.name]: e.target.value,
        });
    };

    const handleUpdateMatchby = (e, v) => setMatchby(v);
    const handleUpdateTimestamp = v => setData(current => {
        return v ? { ...current, timestamp: v.unix() } : current;
    });
    const handleUpdateRangeEnd = v => setData(current => {
        return v ? { ...current, end: v.unix() } : current;
    });
    const handleUpdateProto = (e, v) => setData(current => ({ ...current, proto: v }));
    const handleUpdateFormat = (e, v) => setData(current => ({ ...current, format: v }));

    const handleUpdateInterfaces = (e, v, n) => {
        if (v.length > 1) {
            const n = v.findIndex(a => a === 'any');
            const o = (data.interfaces || []).findIndex(a => a === 'any');
            // clicked on any, clear everything else
            if (n >= 0 && o < 0) v = ['any'];
            // clicked on something else, remove any
            else if (n >= 0 && o >= 0) {
                if (v.length > 0) v.splice(n, 1);
            }
        } else if (v.length === 0) v = ['any'];
        setData(current => ({ ...current, interfaces: v }));
    };

    const handleUpdateCMC = v => {
        setCmc({ name: cmcs[v.target.value], uuid: v.target.value });
        setData(current => ({ ...current, host: '' }));
    };

    const handleCreatePcap = () => {
        if (!data.host) return;
        const { interfaces, end, proto, ...rest } = data;

        // sanitize
        rest.interfaces = interfaces.find(i => i === 'any') ? undefined : interfaces;
        if (matchby === 'Timestamp') {
            rest.tdiff = undefined;
        } else {
            rest.tdiff = (end - rest.timestamp) / 2;
            rest.timestamp += rest.tdiff;
        }
        rest.proto = proto === 'any' ? undefined : proto;

        PcapService.create(data.host, rest, cmc).then(() => {
            navigate('/views/packet_requests');
        });
    };

    const handleCancel = () => {
        navigate('/views/packet_requests');
    };

    const fetchSensorNames = useCallback(async () => {
        if (sensorPerms?.read) {
            const sensors = await SensorsService.getData();
            // console.log('sensors', sensors);
            // console.log('sensors map', (sensors || []).reduce((a, s) => {
            //     return {
            //         ...a,
            //         [s.host]: {
            //             name: _get(s, 'data.name', s.host),
            //             uuid: s.uuid,
            //             host: s.host,
            //             capture: _get(s, 'health_metrics.apps', [])
            //                 .some(app => app.name === 'captured')
            //         }
            //     };
            // }, {})
            // );
            // mapify key by host
            setSensors((sensors || []).reduce((a, s) => {
                return {
                    ...a,
                    [s.host]: {
                        name: _get(s, 'data.name', s.host),
                        uuid: s.uuid,
                        host: s.host,
                        capture: _get(s, 'health_metrics.apps', [])
                            .some(app => app.name === 'captured')
                    }
                };
            }, {})
            );
        }
    }, []);

    const isValid = () => {
        if (MC2CHECK && !cmc) return false;
        if (!data.host) return false;
        if (!Moment(data.timestamp).isValid()) return false;
        if (matchby !== 'Timestamp') {
            if (!Moment(data.end).isValid()) return false;
            if (Moment(data.end).isBefore(Moment(data.timestamp))) return false;
        }
        return checkValidity(daForm.get());
    };

    const fetchCMCs = useCallback(async () => {
        if (cmcPerms?.read) {
            const cmcs = await CMCsDataService.getNames();
            setCmcs(cmcs);

            Object.entries(cmcs || {}).map(([uuid, name]) => {
                sensorPerms?.read && SensorsService.getData('', name).then(sensors => {
                    // update cmc sensor loading indicator
                    setGotSensors(current => {
                        return { ...current, [uuid]: true };
                    });
                    (sensors || []).forEach((s) => {
                        // upsert sensor
                        setSensors(current => {
                            return {
                                ...(current || {}),
                                [s.host]: {
                                    cmc: name,
                                    name: _get(s, 'data.name', s.host),
                                    uuid: s.uuid,
                                    host: s.host,
                                    capture: _get(s, 'health_metrics.apps', [])
                                        .some(app => app.name === 'captured')
                                }
                            };
                        });
                    });
                });
            });
        }
    }, []);

    useEffect(() => {
        if (MC2CHECK) fetchCMCs();
    }, [fetchCMCs]);

    useEffect(() => {
        // on mc2, will fetch sensors one cmc at a time in fetchCMCs call
        if (!MC2CHECK) fetchSensorNames();
    }, [fetchSensorNames]);

    useEffect(() => {
        if (!data.host) {
            setInfo({});
            return;
        }

        if (stats[data.host]) {
            const min = Moment(_get(stats[data.host], 'full.oldest'));
            const max = Moment(_get(stats[data.host], 'full.latest'));

            setInterfaces((stats[data.host].interfaces || []).concat('any'));
            setInfo({ min, max });
            return;
        }
        const sensor = sensors[data.host]; // .find(s => s.host === data.host);
        if (!sensor) return;

        (async () => {
            const d = await SensorsService.getPcapStats(sensor.uuid, cmc);
            if (d) {
                // stash stats so we only try to get once
                setStats(current => ({ ...current, [data.host]: d }));

                const min = Moment(_get(d, 'full.oldest'));
                const max = Moment(_get(d, 'full.latest'));
                setInterfaces((d.interfaces || []).concat('any'));
                setInfo({ min, max });
            }
        })();
    }, [sensors, data.host, cmc]);

    // alert/mtdt => tune - takes you here with a seed pcap request
    useEffect(() => {
        if (!sensors) return;

        const query = {};
        const routeQuery = new URLSearchParams(location.search);
        for (const [k, v] of routeQuery) {
            if (v) query[k] = v;
        }
        setData(current => ({
            ...current,
            ...query,
            // defense against the dark arts of string parameters
            timestamp: +(query.timestamp || current.timestamp),
        }));
    }, [sensors, location.search]);

    return (
        <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box mt={5} p={5}>
                        <Box className={classes.warningMessage} display={(process.env.BRICATA_APPLIANCE_TYPE === 'mc2' && !cmcPerms.read) ? 'flex' : 'none'}>
                            Read CMC permission required
                        </Box>
                        <Box className={classes.warningMessage} display={!sensorPerms.read ? 'flex' : 'none'}>
                            Read Sensor permission required
                        </Box>
                        <Grid container direction="column">
                            <div className={classes.control}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}>
                                    {MC2CHECK && <Grid style={{ padding: 10 }}>
                                        <FormControl
                                            style={{ width: 300, marginRight: 25 }}>
                                            <InputLabel id="sensor-select-label">CMC</InputLabel>
                                            <Select
                                                labelId="cmc-select-label"
                                                id="cmc-select"
                                                label="cmc"
                                                aria-label="cmc"
                                                value={(cmc || { uuid: '' }).uuid}
                                                name="cmc"
                                                onChange={handleUpdateCMC}
                                                input={<Input id="cmc-select-input" />}
                                                MenuProps={MenuProps}>
                                                {Object.entries(cmcs || {}).map(([uuid, name]) => (
                                                    <MenuItem key={uuid} value={uuid}>
                                                        <Grid container
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            wrap='nowrap'>
                                                            <Grid>{name}</Grid>
                                                        </Grid>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>}

                                    <Grid style={{ padding: 10 }}>
                                        <FormControl style={{ width: 300 }}>
                                            <InputLabel id="sensor-select-label">Sensor</InputLabel>
                                            <Select
                                                disabled={MC2CHECK && !cmc}
                                                labelId="sensor-select-label"
                                                id="sensor-select"
                                                label="Sensor"
                                                aria-label="Sensor"
                                                value={data.host}
                                                name="host"
                                                onChange={v => handleUpdateData(v)}
                                                input={<Input id="sensor-select-input" />}
                                                renderValue={(sel) => sel}
                                                MenuProps={MenuProps}>
                                                {Object.entries(sensors || {})
                                                    .map(([host, s]) => s)
                                                    .filter(s => !MC2CHECK || s.cmc === (cmc || {}).name)
                                                    .map((s) => (
                                                        <MenuItem key={s.uuid} value={s.host} disabled={!s.capture}>
                                                            <Grid container
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="space-between"
                                                                wrap='nowrap'>
                                                                <Grid>{s.name}</Grid>
                                                                {/* avoid qa confusion */}
                                                                { s.name !== s.host &&
                                                                    <Grid className={classes.info}>{s.host}</Grid>
                                                                }
                                                            </Grid>
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {(MC2CHECK && !gotSensors[(cmc || {}).uuid]) &&
                                        <Grid style={{ paddingTop: 20 }}>
                                            <CircularProgress size={20} style={{ marginRight: 10 }}/>
                                        </Grid>
                                    }
                                </Grid>
                            </div>

                            <div className={classes.control}>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}>
                                    <Grid style={{ padding: 10 }}>
                                        <InputLabel
                                            className={classes.notCrammed}
                                            id="matchby-label">Match by</InputLabel>
                                        <ToggleButtonGroup
                                            disabled={MC2CHECK && !cmc}
                                            className={classes.primaryButtonGroup}
                                            exclusive
                                            id="matchby"
                                            size="small"
                                            value={matchby}
                                            onChange={handleUpdateMatchby}
                                            label="Match packets by"
                                            aria-label="match by">
                                            <ToggleButton
                                                disabled={MC2CHECK && !cmc}
                                                value="Timestamp"
                                                aria-label="Timestamp">
                                                Timestamp
                                            </ToggleButton>
                                            <ToggleButton
                                                disabled={MC2CHECK && !cmc}
                                                value="Range"
                                                aria-label="Range">
                                                Time Range
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>

                                    <Grid style={{ padding: 10 }}>
                                        {matchby === 'Timestamp' &&
                                            <div className={classes.datePicker}>
                                                <DateTimePicker
                                                    disabled={MC2CHECK && !cmc}
                                                    label="around"
                                                    ampm={false}
                                                    value={Moment.unix(data.timestamp)}
                                                    onChange={handleUpdateTimestamp}
                                                    inputFormat={theme.date.long}
                                                    disableMaskedInput={true}
                                                    showTodayButton={true}/>
                                            </div>
                                        }

                                        {matchby === 'Range' &&
                                            <React.Fragment>
                                                <div className={classes.datePicker}>
                                                    <DateTimePicker
                                                        disabled={MC2CHECK && !cmc}
                                                        label="From"
                                                        ampm={false}
                                                        value={Moment.unix(data.timestamp)}
                                                        onChange={handleUpdateTimestamp}
                                                        inputFormat={theme.date.long}
                                                        disableMaskedInput={true}
                                                        showTodayButton={true}/>
                                                </div>
                                                <div
                                                    className={classes.datePicker}
                                                    style={{ marginLeft: 10 }}>
                                                    <DateTimePicker
                                                        disabled={MC2CHECK && !cmc}
                                                        label="To"
                                                        ampm={false}
                                                        value={Moment.unix(data.end)}
                                                        onChange={handleUpdateRangeEnd}
                                                        inputFormat={theme.date.long}
                                                        disableMaskedInput={true}
                                                        showTodayButton={true}
                                                        minDate={Moment.unix(data.timestamp)}/>
                                                </div>
                                            </React.Fragment>
                                        }
                                    </Grid>
                                    <Grid style={{ alignSelf: 'flex-end', paddingBottom: 20 }}>
                                        {info.min && <div className={classes.minmax}>
                                            Available data:
                                            <span>{info.min.format(theme.date.long)}</span>-
                                            <span>{info.max.format(theme.date.long)}</span>
                                        </div>}
                                    </Grid>
                                </Grid>
                            </div>

                            <div className={classes.control}>
                                <FormControl>
                                    <FormLabel
                                        className={classes.notCrammed}
                                        id="interfaces-label">Interface</FormLabel>
                                    <ToggleButtonGroup
                                        disabled={MC2CHECK && !cmc}
                                        className={classes.primaryButtonGroup}
                                        id="interfaces"
                                        value={data.interfaces}
                                        onChange={handleUpdateInterfaces}
                                        size="small"
                                        aria-label="interfaces">
                                        {interfaces.map(p => (
                                            <ToggleButton
                                                disabled={MC2CHECK && !cmc}
                                                key={p}
                                                value={p}
                                                aria-label={p}>
                                                {p}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </FormControl>
                            </div>

                            <div className={classes.control}>
                                <FormControl>
                                    <FormLabel
                                        className={classes.notCrammed}
                                        id="interfaces-label">IP Protocol</FormLabel>
                                    <ToggleButtonGroup
                                        disabled={MC2CHECK && !cmc}
                                        className={classes.primaryButtonGroup}
                                        id="proto"
                                        value={data.proto}
                                        onChange={handleUpdateProto}
                                        size="small"
                                        aria-label="ip protocol"
                                        exclusive>
                                        {protos.map(p => (
                                            <ToggleButton
                                                disabled={MC2CHECK && !cmc}
                                                size="small"
                                                key={p}
                                                value={p}
                                                aria-label={p}>
                                                {p}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </FormControl>
                            </div>

                            <div>
                                <FormControl>
                                    <Validator
                                        disabled={MC2CHECK && !cmc}
                                        style={{ width: 300, marginRight: 10 }}
                                        label="Source IP"
                                        aria-label="Source IP"
                                        variant="outlined"
                                        margin="normal"
                                        value={data.src || ''}
                                        name="src"
                                        valueType="ipRange"
                                        allowEmpty={true}
                                        onChange={handleUpdateData}/>
                                </FormControl>
                                <FormControl>
                                    <Validator
                                        disabled={MC2CHECK && !cmc}
                                        style={{ width: 200 }}
                                        label="Source Port"
                                        aria-label="Source Port"
                                        variant="outlined"
                                        margin="normal"
                                        value={data.sport || ''}
                                        name="sport"
                                        valueType="port"
                                        allowEmpty={true}
                                        onChange={handleUpdateData}/>
                                </FormControl>
                            </div>

                            <div>
                                <FormControl>
                                    <Validator
                                        disabled={MC2CHECK && !cmc}
                                        style={{ width: 300, marginRight: 10 }}
                                        label="Destination IP"
                                        aria-label="Destination IP"
                                        variant="outlined"
                                        margin="normal"
                                        value={data.dst || ''}
                                        name="dst"
                                        valueType="ipRange"
                                        allowEmpty={true}
                                        onChange={handleUpdateData}/>
                                </FormControl>
                                <FormControl>
                                    <Validator
                                        disabled={MC2CHECK && !cmc}
                                        style={{ width: 200 }}
                                        label="Destination Port"
                                        aria-label="Destination Port"
                                        variant="outlined"
                                        margin="normal"
                                        value={data.dport || ''}
                                        name="dport"
                                        valueType="port"
                                        allowEmpty={true}
                                        onChange={handleUpdateData}/>
                                </FormControl>
                            </div>

                            <div className={classes.control}>
                                <FormControl>
                                    <FormLabel
                                        className={classes.notCrammed}
                                        id="format-label">File Format</FormLabel>
                                    <ToggleButtonGroup
                                        disabled={MC2CHECK && !cmc}
                                        className={classes.primaryButtonGroup}
                                        id="format"
                                        value={data.format}
                                        onChange={handleUpdateFormat}
                                        size="small"
                                        aria-label="File Format"
                                        exclusive>
                                        <ToggleButton
                                            disabled={MC2CHECK && !cmc}
                                            value="pcap"
                                            aria-label="pcap">
                                        pcap
                                        </ToggleButton>
                                        <ToggleButton
                                            disabled={MC2CHECK && !cmc}
                                            value="pcapng"
                                            aria-label="pcapng">
                                        pcapng
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </FormControl>
                            </div>

                            <Grid item>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.buttonRight}
                                    aria-label="create"
                                    disabled={!isValid()}
                                    onClick={handleCreatePcap}>
                                    Request Pcap
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={classes.buttonRight}
                                    aria-label="Cancel"
                                    onClick={handleCancel}>
                                    Cancel
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default CreatePcap;
